import { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search,
    Eye,
    Edit,
    Trash2,
    Plus,
    RefreshCw,
    Calendar,
    CreditCard,
    CheckCircle,
    XCircle,
    Filter,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import {
    useGetAllMembershipPlansQuery,
    useDeleteMembershipPlanMutation,
    MembershipPlan
} from '../../store/slices/apiSlice';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';

const MembershipPlanManagement = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'true' | 'false'>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    
    // Modal states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<MembershipPlan | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Fetch membership plans
    const filterParams = statusFilter !== 'all' ? { is_active: statusFilter === 'true' } : {};
    const { data, isLoading, error, refetch } = useGetAllMembershipPlansQuery(filterParams);
    const [deletePlan, { isLoading: isDeleting }] = useDeleteMembershipPlanMutation();

    // Extract plans from API response
    const plans = data?.data || [];

    // Filter plans based on search
    const filteredPlans = plans.filter((plan: MembershipPlan) => {
        const matchesSearch =
            plan.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            plan.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            plan.access_level?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    // Pagination logic
    const totalPages = Math.ceil(filteredPlans.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedPlans = filteredPlans.slice(startIndex, startIndex + itemsPerPage);

    // Statistics
    const activePlans = plans.filter(p => p.is_active).length;
    const inactivePlans = plans.filter(p => !p.is_active).length;
    const totalPlans = plans.length;

    // Handle create - navigate to create page
    const handleCreate = () => {
        navigate('/admin/membership-management/create');
    };

    // Handle edit - navigate to edit page
    const handleEdit = (plan: MembershipPlan) => {
        navigate(`/admin/membership-management/edit/${plan.plan_id}`);
    };

    // Handle view - navigate to view page
    const handleView = (plan: MembershipPlan) => {
        navigate(`/admin/membership-management/view/${plan.plan_id}`);
    };

    // Handle items per page change
    const handleItemsPerPageChange = (newItemsPerPage: number) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page when changing items per page
    };

    // Handle delete modal open
    const handleDeleteModalOpen = (plan: MembershipPlan) => {
        setSelectedPlan(plan);
        setDeleteError(null); // Reset error when opening modal
        setShowDeleteModal(true);
    };

    // Handle delete
    const handleDelete = async () => {
        if (!selectedPlan) return;

        // Reset error before attempting delete
        setDeleteError(null);

        try {
            const result = await deletePlan(selectedPlan.plan_id).unwrap();
            
            // Check if deletion was actually successful
            if (result?.success === false) {
                throw new Error(result?.message || 'Failed to delete membership plan');
            }
            
            // Only show success if truly successful
            toast.success('Membership plan deleted successfully!', {
                duration: 4000,
                icon: '✅',
            });
            setShowDeleteModal(false);
            setSelectedPlan(null);
            setDeleteError(null);
            refetch();
        } catch (error: any) {
            console.error('Failed to delete plan:', error);
            console.log('Error object:', JSON.stringify(error, null, 2));
            
            // Extract error message from various possible structures
            let errorMessage = 'Failed to delete membership plan. Please try again.';
            
            // Check multiple possible error structures
            if (error?.data?.message) {
                errorMessage = error.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            } else if (error?.status === 400) {
                errorMessage = 'Cannot delete plan with active memberships. Please deactivate it instead.';
            }
            
            // Set error state to show in modal
            setDeleteError(errorMessage);
            
            // Show error toast with longer duration and prominent styling
            toast.error(errorMessage, {
                duration: 8000,
                style: {
                    maxWidth: '600px',
                    padding: '16px',
                    fontSize: '15px',
                    fontWeight: '500',
                },
                icon: '❌',
            });
            
            // Keep modal open so user can see the error
        }
    };

    // Format currency
    const formatCurrency = (amount: string | number) => {
        const num = typeof amount === 'string' ? parseFloat(amount) : amount;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(num);
    };

    // Get status color
    const getStatusColor = (isActive: boolean) => {
        return isActive 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800';
    };

    // Get access level color
    const getAccessLevelColor = (level: string | null) => {
        switch (level) {
            case 'platinum': return 'bg-purple-100 text-purple-800';
            case 'gold': return 'bg-yellow-100 text-yellow-800';
            case 'silver': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            const startPage = Math.max(1, currentPage - 2);
            const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
        }

        return pages;
    };

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-center">
                        <XCircle className="h-6 w-6 text-red-600 mr-3" />
                        <div>
                            <h3 className="text-lg font-semibold text-red-800">Error Loading Membership Plans</h3>
                            <p className="text-red-600 mt-1">There was an error loading the membership plans. Please try again.</p>
                            <button
                                onClick={() => refetch()}
                                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Membership Plan Management</h1>
                        <p className="text-gray-600">Manage membership plans and pricing</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={handleCreate}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Create Plan
                        </button>
                        <button
                            onClick={() => refetch()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Plans</p>
                            <p className="text-3xl font-bold text-gray-900">{totalPlans}</p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-full">
                            <CreditCard className="h-6 w-6 text-blue-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Active Plans</p>
                            <p className="text-3xl font-bold text-green-600">{activePlans}</p>
                        </div>
                        <div className="p-3 bg-green-100 rounded-full">
                            <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Inactive Plans</p>
                            <p className="text-3xl font-bold text-gray-600">{inactivePlans}</p>
                        </div>
                        <div className="p-3 bg-gray-100 rounded-full">
                            <XCircle className="h-6 w-6 text-gray-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Search plans..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <select
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value as 'all' | 'true' | 'false');
                                    setCurrentPage(1);
                                }}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Status</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Plans Cards */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">Membership Plans ({filteredPlans.length})</h2>
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Items per page:</label>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={12}>12</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                    </div>
                </div>

                {filteredPlans.length === 0 ? (
                    <div className="p-12 text-center">
                        <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No membership plans found</h3>
                        <p className="text-gray-600 mb-4">No plans match your current filters.</p>
                        <button
                            onClick={handleCreate}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            Create Your First Plan
                        </button>
                    </div>
                ) : (
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {paginatedPlans.map((plan: MembershipPlan) => (
                                <div key={plan.plan_id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                                    {/* Card Header */}
                                    <div className="p-6 border-b border-gray-200">
                                        <div className="flex items-start justify-between mb-2">
                                            <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(plan.is_active)}`}>
                                                {plan.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        {plan.description && (
                                            <p className="text-sm text-gray-600 line-clamp-2 mt-2">{plan.description}</p>
                                        )}
                                    </div>

                                    {/* Card Body */}
                                    <div className="p-6 space-y-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">Price</span>
                                            <span className="text-xl font-bold text-gray-900">{formatCurrency(plan.price)}</span>
                                        </div>
                                        {plan.payment_type === 'monthly_installment' && plan.monthly_installment_amount && (
                                            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                                <span className="text-sm text-gray-600">Monthly Payment</span>
                                                <span className="text-lg font-semibold text-blue-600">
                                                    {formatCurrency(plan.monthly_installment_amount)}/mo
                                                </span>
                                            </div>
                                        )}
                                        
                                        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                                            <div>
                                                <span className="text-xs text-gray-500 flex items-center mb-1">
                                                    <Calendar className="h-3 w-3 mr-1" />
                                                    Duration
                                                </span>
                                                <p className="text-sm font-medium text-gray-900">{plan.duration_days} days</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500 mb-1 block">Bookings/Month</span>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {plan.max_bookings_per_month === 0 ? 'Unlimited' : plan.max_bookings_per_month}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="pt-2 border-t border-gray-100 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-gray-500">Payment Type</span>
                                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 capitalize">
                                                    {plan.payment_type === 'monthly_installment' ? 'Monthly Installment' : 'One Time'}
                                                </span>
                                            </div>
                                            {plan.access_level && (
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs text-gray-500">Access Level</span>
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getAccessLevelColor(plan.access_level)}`}>
                                                        {plan.access_level}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Card Footer */}
                                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-2">
                                        <button
                                            onClick={() => handleView(plan)}
                                            className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                                            title="View plan details"
                                        >
                                            <Eye className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => handleEdit(plan)}
                                            className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                                            title="Edit plan"
                                        >
                                            <Edit className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteModalOpen(plan)}
                                            className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50 transition-colors"
                                            title="Delete plan"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-white rounded-lg shadow border border-gray-100 p-4 mt-6">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                                Showing page {currentPage} of {totalPages} ({filteredPlans.length} total plans)
                            </div>

                            <div className="flex items-center space-x-2">
                                {/* First Page */}
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="First page"
                                >
                                    <ChevronsLeft className="w-4 h-4" />
                                </button>

                                {/* Previous Page */}
                                <button
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Previous page"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>

                                {/* Page Numbers */}
                                <div className="flex items-center space-x-1">
                                    {getPageNumbers().map((pageNum) => (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                currentPage === pageNum
                                                    ? 'bg-blue-600 text-white'
                                                    : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    ))}
                                </div>

                                {/* Next Page */}
                                <button
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Next page"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>

                                {/* Last Page */}
                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Last page"
                                >
                                    <ChevronsRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setDeleteError(null);
                }}
                title="Delete Membership Plan"
            >
                <div className="space-y-4">
                    {selectedPlan && (
                        <>
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                                    <Trash2 className="h-6 w-6 text-red-600" />
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-900">{selectedPlan.name}</h4>
                                    <p className="text-sm text-gray-600">{formatCurrency(selectedPlan.price)}</p>
                                </div>
                            </div>

                            {/* Show delete error if exists */}
                            {deleteError && (
                                <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 animate-shake">
                                    <div className="flex items-start space-x-3">
                                        <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="text-red-900 font-bold text-base mb-1">⚠️ Cannot Delete Plan</p>
                                            <p className="text-red-700 text-sm leading-relaxed">
                                                {deleteError}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-3">
                                <p className="text-gray-600">
                                    Are you sure you want to delete this membership plan? This action cannot be undone.
                                </p>
                                {selectedPlan.memberships && selectedPlan.memberships.length > 0 && (
                                    <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                                        <div className="flex items-start space-x-2">
                                            <XCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-yellow-900 font-semibold text-sm">⚠️ Warning</p>
                                                <p className="text-yellow-700 text-sm mt-1">
                                                    This plan has {selectedPlan.memberships.length} active membership(s). 
                                                    You cannot delete plans with active memberships. Please deactivate it instead.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    <div className="flex gap-3 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowDeleteModal(false);
                                setDeleteError(null);
                            }}
                            className="flex-1"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="flex-1"
                        >
                            {isDeleting ? 'Deleting...' : 'Delete Plan'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default MembershipPlanManagement;

